import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ExternalLink, Trash2 } from 'lucide-react';

const typeTones = {
  article: '#EEEADF',
  person:  '#EAE5DA',
  movie:   '#E8E3D8',
  topic:   '#F1ECE3',
  image:   '#E9E5DB',
  video:   '#E7E2D8',
  audio:   '#ECE7DC',
  file:    '#F2EEE4',
  link:    '#EFEAE0',
};

const ClipCard = ({ clip, isHighlighted, onDelete, onClick }) => {
  const tone = typeTones[clip.type] || '#F2EEE4';

  const handleCardClick = useCallback((e) => {
    if (e.target.closest('button')) return;
    onClick?.(clip);
  }, [clip, onClick]);

  const handleOpenLink = useCallback((e) => {
    e.stopPropagation();
    if (clip.url) window.open(clip.url, '_blank');
  }, [clip.url]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    onDelete(clip.id);
  }, [clip.id, onDelete]);

  return (
    <article
      onClick={handleCardClick}
      className="cursor-pointer transition-all active:scale-[0.97] rounded-[10px] overflow-hidden p-3"
      style={{
        backgroundColor: tone,
        border: isHighlighted ? '1px solid #064e3b' : '1px solid rgba(0,0,0,0.08)',
        boxShadow: isHighlighted
          ? '0 0 0 2px rgba(6,78,59,0.15), 0 1px 4px rgba(0,0,0,0.05)'
          : '0 1px 4px rgba(0,0,0,0.05)',
      }}
    >
      {clip.fileData && (
        <div className="mb-2.5 rounded-[6px] overflow-hidden">
          {clip.type === 'image' && (
            <img src={clip.fileData} alt={clip.title} className="w-full h-20 object-cover" loading="lazy" />
          )}
          {clip.type === 'video' && (
            <video src={clip.fileData} className="w-full h-20 object-cover" controls preload="metadata" />
          )}
          {clip.type === 'audio' && (
            <audio src={clip.fileData} className="w-full" controls preload="metadata" />
          )}
        </div>
      )}

      {/* Type pill */}
      <div className="mb-2.5">
        <span
          className="px-2 py-1 rounded-full text-[9px] uppercase font-semibold"
          style={{ letterSpacing: '1.5px', background: 'rgba(0,0,0,0.07)', color: 'rgba(0,0,0,0.55)' }}
        >
          {clip.type}
        </span>
      </div>

      {/* Title */}
      <h4 className="font-instrument text-[20px] leading-[24px] text-[#111] line-clamp-2 mb-3">
        {clip.title}
      </h4>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 pt-2.5" style={{ borderTop: '1px solid rgba(0,0,0,0.07)' }}>
        <span className="text-[10px] uppercase font-semibold" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.35)' }}>
          {clip.addedAt}
        </span>
        <div className="flex items-center gap-1">
          {clip.url && (
            <button
              onClick={handleOpenLink}
              className="w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.08)' }}
              aria-label={`Open link to ${clip.title}`}
            >
              <ExternalLink className="w-3.5 h-3.5" style={{ color: 'rgba(0,0,0,0.50)' }} />
            </button>
          )}
          <button
            onClick={handleDelete}
            className="w-7 h-7 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.08)' }}
            aria-label={`Remove ${clip.title}`}
          >
            <Trash2 className="w-3.5 h-3.5" style={{ color: 'rgba(0,0,0,0.50)' }} />
          </button>
        </div>
      </div>
    </article>
  );
};

ClipCard.propTypes = {
  clip: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string,
    fileData: PropTypes.string,
    fileType: PropTypes.string,
    addedAt: PropTypes.string.isRequired,
  }).isRequired,
  isHighlighted: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onClick: PropTypes.func,
};

export default ClipCard;
